import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import type { Booking } from "@/types/Booking";

export type SlotHolidayFlowState = {
  step: number;
  setStep: (step: number) => void;
  selectedBookingIds: string[];
  setSelectedBookingIds: (bookingIds: string[]) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  booking?: Booking;
  resetFlow: () => void;
};

const SlotHolidayFlowContext = createContext<SlotHolidayFlowState | undefined>(undefined);

export const useSlotHolidayFlow = () => {
  const context = useContext(SlotHolidayFlowContext);
  if (!context) {
    throw new Error("useSlotHolidayFlow must be used within a SlotHolidayProvider");
  }
  return context;
};

type SlotHolidayProviderProps = {
  children: ReactNode;
  booking?: Booking;
};

export const SlotHolidayProvider = ({ children, booking }: SlotHolidayProviderProps) => {
  const [step, setStep] = useState(1);
  const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reset all flow state to initial values
  const resetFlow = () => {
    setStep(1);
    setSelectedBookingIds([]);
    setIsLoading(false);
    setError(null);
  };

  const value: SlotHolidayFlowState = {
    step,
    setStep,
    selectedBookingIds,
    setSelectedBookingIds,
    isLoading,
    setIsLoading,
    error,
    setError,
    booking,
    resetFlow,
  };

  return <SlotHolidayFlowContext.Provider value={value}>{children}</SlotHolidayFlowContext.Provider>;
};
